import { css, html } from 'lit';
import { property } from 'lit/decorators.js';
import { KryptonElement } from './krypton-element';

const months = {
    "full": ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    "short": ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
}

class DaylightSavingsTime {

    private static offsets: Map<number, DaylightSavingsTimeOffset> = new Map<number, DaylightSavingsTimeOffset>();
    static isDstModifier: number = 25200000; // 7 * 60 * 60 * 1000
    static notDstModifier: number = 28800000; // 8 * 60 * 60 * 1000

    static newOffset(year: number) {
        // US DST Begin time 2nd Sunday of March
        let startDate = new Date(year, 2, 14);
        startDate.setDate(startDate.getDate() - startDate.getDay());
        startDate.setUTCHours(8 + 2); // 2AM PST
        // US DST End time 1st Sunday of November
        let endDate = new Date(year, 10, 7);
        endDate.setDate(endDate.getDate() - endDate.getDay());
        endDate.setUTCHours(7 + 2); // 2AM PDT
        return {
            start: startDate,
            end: endDate
        };
    }

    static getOffset(year: number): DaylightSavingsTimeOffset {
        let offset;
        if (!this.offsets.has(year)) {
            this.offsets.set(year, offset = new DaylightSavingsTimeOffset(year));
        } else {
            offset = this.offsets.get(year);
        }
        return offset;
    }

    static isDaylightSavingsTime(timeMillis: number): boolean {
        const date = new Date(timeMillis), dstOffset = this.getOffset(date.getUTCFullYear());
        return (date >= dstOffset.start) && (date < dstOffset.end);
    }

    static adjust(timeMillis: number) {
        const isDaylightSavingsTime = DaylightSavingsTime.isDaylightSavingsTime(timeMillis);
        return new Date(timeMillis - (isDaylightSavingsTime ? DaylightSavingsTime.isDstModifier : DaylightSavingsTime.notDstModifier));
    }

}

class DaylightSavingsTimeOffset {

    start: Date;
    end: Date;

    constructor(year: number) {
        // US DST Begin time 2nd Sunday of March
        let startDate = new Date(year, 2, 14);
        startDate.setDate(startDate.getDate() - startDate.getDay());
        startDate.setUTCHours(8 + 2); // 2AM PST
        this.start = startDate;
        // US DST End time 1st Sunday of November
        let endDate = new Date(year, 10, 7);
        endDate.setDate(endDate.getDate() - endDate.getDay());
        endDate.setUTCHours(7 + 2); // 2AM PDT
        this.end = endDate;
    }

}

class ServerTime {

    time: number;
    delta: number;

    constructor(serverTime: number, lastTick: number) {
        this.time = serverTime;
        this.delta = serverTime - (lastTick || serverTime);
    }

    toDate(withMillisFrom: number) {
        let date = new Date(this.time);
        if (withMillisFrom) {
            date.setMilliseconds(new Date(withMillisFrom).getMilliseconds());
        }
        return date
    }

}

export class PCTDateTime {

    static timezone: string = "PCT";
    private date: Date;
    public shortMonth: boolean = false;
    public shortTime: boolean = false;
    public hour24: boolean = true;

    constructor(timeMillis: number, options?: any) {
        this.date = DaylightSavingsTime.adjust(timeMillis);
        if (options) {
            this.shortMonth = Object.is(options.shortMonth, undefined) ? this.shortMonth : options.shortMonth;
            this.shortTime = Object.is(options.shortTime, undefined) ? this.shortTime : options.shortTime;
            this.hour24 = Object.is(options.hour24, undefined) ? this.hour24 : options.hour24;
        }
    }

    json(): {
        year: number,
        month: number,
        monthName: string,
        day: number,
        hours: number,
        minutes: number,
        seconds: number,
        meridiem: string,
        time: string
    } {
        return {
            year: this.year,
            month: this.month,
            monthName: this.monthName,
            day: this.day,
            hours: this.hours,
            minutes: this.minutes,
            seconds: this.seconds,
            meridiem: this.meridiem,
            time: this.time
        };
    }

    toString() {
        return this.monthName + " " + this.day + ", " + this.year + " " + this.time;
    }

    get year(): number {
        return this.date.getUTCFullYear();
    }

    get month(): number {
        return this.date.getUTCMonth();
    }

    get monthName(): string {
        return this.shortMonth ? months.short[this.month] : months.full[this.month];
    }

    get day(): number {
        return this.date.getUTCDate();
    }

    get hours(): number {
        if (this.hour24) {
            return this.date.getUTCHours();
        } else {
            return this.date.getUTCHours() <= 12 ? this.date.getUTCHours() : this.date.getUTCHours() % 12;
        }
    }

    get minutes(): number {
        return this.date.getUTCMinutes();
    }

    get seconds(): number {
        return this.date.getUTCSeconds();
    }

    get meridiem(): string {
        return this.date.getUTCHours() < 12 ? "AM" : "PM";
    }

    get timezone(): string {
        return PCTDateTime.timezone;
    }

    get time(): string {
        if (this.shortTime) {
            return this.hours
                + ":" + this.pad(this.minutes)
                + ":" + this.pad(this.seconds)
                + (this.hour24 ? "" : " " + this.meridiem)
        } else {
            return this.pad(this.hours)
                + ":" + this.pad(this.minutes)
                + ":" + this.pad(this.seconds)
                + (this.hour24 ? "" : " " + this.meridiem)
                + " " + PCTDateTime.timezone;
        }
    }

    private pad(value: number): string {
        return value.toString().padStart(2, "0");
    }

}

export abstract class PCTClock {

    protected nextTickTimer: number = 0;
    protected dateTime: PCTDateTime = new PCTDateTime(Date.now());
    protected lastTick: number = 0;
    protected onTickCallbacks: ((pctDateTime: PCTDateTime) => any)[] = [];

    public options: { shortMonth: boolean, shortTime: boolean, hour24: boolean } = {
        shortMonth: false,
        shortTime: false,
        hour24: false
    };

    protected async tick() {
        this.stop();
        this.dateTime = this.doTick();
        this.lastTick = Date.now();
        for (let i = 0, onTickCallback: Function; onTickCallback = this.onTickCallbacks[i]; i++) onTickCallback(this.dateTime);
        let nextTickDelay = this.getNextTickDelay();
        //@ts-ignore
        this.nextTickTimer = setTimeout(this.tick.bind(this), nextTickDelay);
    }

    protected abstract doTick(): PCTDateTime;

    protected abstract getNextTickDelay(): number;

    public start() {
        this.tick();
    }

    public stop() {
        if (this.nextTickTimer) {
            clearTimeout(this.nextTickTimer);
            this.nextTickTimer = 0;
        }
    }

    public onTick(callback: (pctDateTime: PCTDateTime) => any) {
        this.onTickCallbacks.push(callback);
    }

    public getDateTime(): PCTDateTime {
        return this.dateTime;
    }

}

export class UnsynchronizedClock extends PCTClock {

    protected doTick(): PCTDateTime {
        return new PCTDateTime(Date.now(), this.options);
    }

    protected getNextTickDelay(): number {
        return 1000;
    }

}

export class SynchronizedPCTClock extends PCTClock {

    public static TIME_ANNOUNCER_URL: string = "/timeAnnouncer";
    private static syncFailLimit = 10;
    private syncFailCount: number = 0;
    private static staleLimit: number = 1500;
    private serverTime: ServerTime = new ServerTime(Date.now(), this.lastTick);

    protected doTick(): PCTDateTime {
        const now = Date.now();
        // const isStale = Noise.boolean(() => Math.abs(now - this.lastTick) > SynchronizedPCTClock.staleLimit, 3, "isStale");
        let diff = Math.abs(now - this.lastTick);
        const isStale = diff > SynchronizedPCTClock.staleLimit;
        if (isStale) this.sync();
        return new PCTDateTime(now + this.serverTime.delta, this.options);
    }

    protected getNextTickDelay(): number {
        return (1100 - new Date(this.lastTick).getUTCMilliseconds()) % 1000; // Next update at when millis component is 100
    }

    private async sync() {
        if (this.syncFailCount > SynchronizedPCTClock.syncFailLimit) {
            console.warn("SynchronizedPCTClock sync failed, failed count = ", this.syncFailCount, " max fail limit = ", SynchronizedPCTClock.syncFailLimit);
            return;
        }
        this.stop();
        const requestTime = Date.now();

        let fetchSuccess = false;
        try {
            //get server time
            const response = await fetch(SynchronizedPCTClock.TIME_ANNOUNCER_URL);
            const responseJson = await response.json();

            const responseTime = Date.now();

            if (response.ok) {
                fetchSuccess = true;
                this.syncFailCount = 0;
                this.lastTick = requestTime + Math.round((responseTime - requestTime) / 2);
                this.serverTime = new ServerTime(parseInt(responseJson.data.serverTime), this.lastTick);
                this.dateTime = new PCTDateTime(this.serverTime.toDate(this.lastTick).getTime(), this.options);
            }
        } catch (e) {
        }
        if (!fetchSuccess) {
            ++this.syncFailCount;
            if (this.syncFailCount > 200) {// 200 is a big number as 2 to the power of 200 is almost near infinity!
                this.syncFailCount = 1;
            }
            let nextSyncTime = Math.pow(2, this.syncFailCount) * 1000;
            console.warn("Failed SynchronizedPCTClock fetch, next sync time = ", nextSyncTime)
            setTimeout(() => { this.sync() }, nextSyncTime);
        }
    }
}

export class ServerClock extends KryptonElement {

    static clock: PCTClock = new SynchronizedPCTClock();

    static styles = css`
        :host {
            display: flex;
            align-items: center;
        }

        @media only screen and (max-width: 767px) {
            :host(.menu-event){
                opacity: 0;
            }
        }

        #server_clock {
            display: flex;
            justify-content: flex-end;
            flex-wrap: nowrap;
            align-items: center;
        }
        
        #server_clock > span {
            white-space: pre;
        }
        #server_clock .time-display {
            white-space: normal!important;
        }

        @media only screen and (min-width: 320px) and (max-width: 767px) {
            #server_clock {
                display: flex;
                justify-content: flex-end;
                flex-wrap: wrap;
                align-items: center;
            }
        }
    `;

    render() {
        return html`
            <span id="server_clock">
                <span>${this.dateTime?.monthName}&nbsp;${this.dateTime?.day},&nbsp;${this.dateTime?.year}</span>
                <span>&nbsp;${this.dateTime?.time}</span>
            </span>
        `;
    }

    get dateTime(): PCTDateTime {
        return ServerClock.clock.getDateTime();
    }

    get hour24() {
        return ServerClock.clock.options.hour24;
    }

    @property({ type: Boolean, reflect: true, attribute: "hour24" })
    set hour24(hour24: boolean) {
        const oldValue = ServerClock.clock.options.hour24;
        ServerClock.clock.options.hour24 = hour24;
        this.requestUpdate("hour24", oldValue);
    }

    get shortMonth(): boolean {
        return ServerClock.clock.options.shortMonth;
    }

    @property({ type: Boolean, reflect: true, attribute: "short-month" })
    set shortMonth(shortMonth: boolean) {
        const oldValue = ServerClock.clock.options.shortMonth;
        ServerClock.clock.options.shortMonth = shortMonth;
        this.requestUpdate("shortMonth", oldValue);
    }

    get shortTime() {
        return ServerClock.clock.options.shortTime;
    }

    @property({ type: Boolean, reflect: true, attribute: "short-time" })
    set shortTime(shortTime: boolean) {
        const oldValue = ServerClock.clock.options.shortTime;
        ServerClock.clock.options.shortTime = shortTime;
        this.requestUpdate("paddedTime", oldValue);
    }

    connectedCallback() {
        ServerClock.clock.onTick(() => this.requestUpdate());
        ServerClock.clock.start();
        super.connectedCallback();
    }

    onTick(onTickCallback: (pctDateTime: PCTDateTime) => any) {
        ServerClock.clock.onTick(onTickCallback);
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'server-clock': ServerClock;
    }
}
